import { ConfigProvider } from 'antd';
import { WidgetEmpty } from 'components/widgets/WidgetEmpty';
import useAntdTreeA11y from 'hooks/useAntdTreeA11y';

const renderEmpty = () => <WidgetEmpty />;
import { useEffect } from 'react';

export default function ConfigurationProvider({ children }) {
  // Patch antd v4 (.ant-tree) and v5 (.ant-v5-tree) with missing ARIA attrs
  useAntdTreeA11y();

  useEffect(() => {
    const normalizeMessageAccessibility = () => {
      // setting the accessibility attributes generically if not provided
      document.querySelectorAll('.ant-message').forEach((message) => {
        message.setAttribute('role', 'region');
        message.setAttribute('aria-label', 'Notifications');
      });
      document
        .querySelectorAll('.ant-message-custom-content > .anticon[role="img"]')
        .forEach((icon) => {
          icon.setAttribute('aria-hidden', 'true');
        });
      document
        .querySelectorAll(
          '.anticon[role="img"]:not([aria-label]):not([aria-labelledby])',
        )
        .forEach((icon) => icon.setAttribute('aria-hidden', 'true'));
      document
        .querySelectorAll('button:not([aria-label]):not([aria-labelledby])')
        .forEach((button) => {
          if (!button.textContent.trim()) {
            button.setAttribute(
              'aria-label',
              button.getAttribute('title') ||
                button.getAttribute('id') ||
                button.getAttribute('data-test-id') ||
                'Action',
            );
          }
        });

      // Temp solution for antd v4 table as it doesn't provide an option to update header checkbox props
      document.querySelectorAll('.ant-table-thead input[type="checkbox"]').forEach(input => {
        input.setAttribute('aria-label', 'Select all rows');
      })
      document
        .querySelectorAll('input:not([aria-label]):not([aria-labelledby])')
        .forEach((input) => {
          const type = input.getAttribute('type');
          const role = input.getAttribute('role');

          if (type === 'search' || role === 'combobox') {
            input.setAttribute('aria-label', 'Search');
          }
        });
      document
        .querySelectorAll('.ac-data-table-container th:not([aria-labelledby])')
        .forEach((header) => {
          if (!header.textContent.trim()) {
            // adding labels to action column since it doesn't have any title
            const label = document.createElement('span');
            label.textContent = 'Actions';
            label.className = 'sr-only';
            header.appendChild(label);
          }
        });
      document
        .querySelectorAll('.ant-table-content, .ac-table-body')
        .forEach((region) => {
          if (!region.hasAttribute('tabindex')) {
            // table scroll
            region.setAttribute('tabindex', '0');
          }
        });
      document
        .querySelectorAll(
          '[role="textbox"]:not([aria-label]):not([aria-labelledby])',
        )
        .forEach((textbox) => textbox.setAttribute('aria-label', 'Content'));
      document
        .querySelectorAll('a:not([href])[aria-label]')
        .forEach((anchor) => anchor.removeAttribute('aria-label'));
      document
        .querySelectorAll('a[href]:not([aria-label]):not([aria-labelledby])')
        .forEach((link) => {
          if (!link.textContent.trim()) {
            link.setAttribute(
              'aria-label',
              link.getAttribute('title') || 'Open link',
            );
          }
        });
      document
        .querySelectorAll('.ant-upload[role="button"]')
        .forEach((upload) => {
          if (upload.querySelector('button')) {
            upload.removeAttribute('role');
            upload.removeAttribute('tabindex');
          }
        });
      document
        .querySelectorAll('.ant-collapse-header[role="button"]')
        .forEach((header) => {
          if (header.querySelector('input, button, a, select, textarea')) {
            header.removeAttribute('role');
            header.removeAttribute('tabindex');
            header.removeAttribute('aria-expanded');
          }
        });
      document
        .querySelectorAll('.ant-tabs-nav[role="tablist"]')
        .forEach((tabNav) => {
          const tabList = tabNav.querySelector('.ant-tabs-nav-list');

          if (tabList) {
            tabNav.removeAttribute('role');
            tabList.setAttribute('role', 'tablist');
          }
        });
      document.querySelectorAll('input[role="combobox"]').forEach((input) => {
        if (!input.hasAttribute('aria-expanded')) {
          input.setAttribute('aria-expanded', 'false');
        }

        if (input.getAttribute('aria-expanded') === 'false') {
          input.removeAttribute('aria-activedescendant');
        }

        ['aria-owns', 'aria-activedescendant'].forEach((attribute) => {
          const id = input.getAttribute(attribute);
          if (id && !document.getElementById(id)) {
            input.removeAttribute(attribute);
          }
        });
      });
    };

    normalizeMessageAccessibility();
    if (!window.MutationObserver) return undefined;

    const observer = new window.MutationObserver(normalizeMessageAccessibility);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return (
    <ConfigProvider
      renderEmpty={renderEmpty}
      locale={{
        Table: {
          triggerAsc: 'Click to sort in ascending',
          triggerDesc: 'Click to sort in descending',
          cancelSort: 'Click to cancel sorting',
        },
      }}
      csp={{ nonce: 'nonce-e73f1ece5087b8a5ae33998952202202' }}
    >
      {children}
    </ConfigProvider>
  );
}
